import React, { useEffect, useState, useMemo } from 'react';
import { Container, Card, Table, Button, Spinner, Modal, Form } from "react-bootstrap";
import { Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AvatarCentrado from "../modales/AvatarCentrado";

import {API_BASE} from "../../services/apirest";

export default function SolicitudBaja() {
  const rolId = parseInt(localStorage.getItem('rol_id'), 10);
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [loading, setLoading] = useState(true);
  const [solicitudes, setSolicitudes] = useState([]);

  // filtro de estados: "Todos", "Activos" (Visto+Activo), "Inactivos"
  const [filterEstado, setFilterEstado] = useState('Activos');

  // … estados para modals (igual que antes) …
  const [selected, setSelected] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [reasonType, setReasonType]             = useState('');
  const [reasonText, setReasonText]             = useState('');

  const handleOpenDetail = sol => {
    setSelected(sol);
    setShowDetailModal(true);
  };
  const handleCloseDetail = () => {
    setSelected(null);
    setShowDetailModal(false);
  };

  const handleConfirmReason = async () => {
    if (!reasonType) return;
    try {
      const payload = {
        SolicitudId: selected.SolicitudId,
        status: 'Progreso',
        razon_baja: (reasonType === 'Mala práctica' || reasonType === 'Otro')
          ? reasonText.trim()
          : reasonType
      };
      const res = await fetch(`${API_BASE}/solicitudes.php`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.status === 'success' || json.status === 'ok') {
        Swal.fire({
          icon: 'success',
          title: '¡LISTO!',
          text: 'Baja en progreso.',
          didOpen: () => {
            const c = Swal.getContainer();
            if (c) c.style.zIndex = '13000';
          }
        });
        setShowDetailModal(false);
        // no re-fetching entire list; mejor filtrar localmente:
        setSolicitudes(prev =>
         prev.map(s =>
           s.SolicitudId === selected.SolicitudId
             ? {
                 ...s,
                 status: 'Progreso',
                 razon_baja:
                   (reasonType === 'Mala práctica' || reasonType === 'Otro')
                     ? reasonText.trim()
                     : reasonType
               }
             : s
         )
       );
      } else {
        throw new Error(json.result?.error_msg);
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Falló la petición',
        didOpen: () => {
          const c = Swal.getContainer();
          if (c) c.style.zIndex = '13000';
        }
      });
    }
  };

  // carga según filtro
  useEffect(() => {
   if (![2, 3, 4].includes(rolId)) {
      setLoading(false);
      return;
    };
   (async () => {
      setLoading(true);
      try {
        let items = [];
        if (filterEstado === 'Activos') {
          // sólo status=Activo (y opcionalmente Estado=Visto)
          const res = await fetch(`${API_BASE}/solicitudes.php?status=Activo`, { headers });
          items = (await res.json()).result.solicitudes || [];
          // si quieres seguir filtrando sólo los ya completados:
          items = items.filter(s => s.Estado === 'Visto');
        }
        else if (filterEstado === 'Inactivos') {
          const res = await fetch(`${API_BASE}/solicitudes.php?status=Inactivo`, { headers });
          items = (await res.json()).result.solicitudes || [];
        }
        else { 
          // Todos: activo, progreso e inactivo
          const statuses = ['Activo','Progreso','Inactivo'];
          const respuestas = await Promise.all(
            statuses.map(st =>
              fetch(`${API_BASE}/solicitudes.php?status=${encodeURIComponent(st)}`, { headers })
                .then(r => r.json())
                .then(d => d.result.solicitudes || [])
            )
          );
          items = respuestas.flat();
          // si sólo quieres Progreso y Activo cuando ya están Visto:
          items = items.filter(s =>
            s.status === 'Inactivo' ||
            (s.Estado === 'Visto' && (s.status === 'Activo' || s.status === 'Progreso'))
          );
        }
        setSolicitudes(items);
      } catch {
        setSolicitudes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [filterEstado, rolId, headers]);

  if (![2, 3, 4].includes(rolId)) return <Navigate to='/' replace />;
  if (loading) {
    return (
      <Container className='d-flex justify-content-center' style={{ height: '60vh' }}>
        <Spinner animation='border' />
      </Container>
    );
  }

  // aplicación del filtro avanzado:
   const filtered = solicitudes.filter(s => {
    if (filterEstado === 'Activos') {
      return s.Estado === 'Visto' && s.status === 'Activo';
    }
    if (filterEstado === 'Inactivos') {
      return s.status === 'Inactivo';
    }
    
    return (
      (s.Estado === 'Visto' && (s.status === 'Activo' || s.status === 'Progreso'))
      || s.status === 'Inactivo'
    );
  });

  // helper para etiqueta de status
  const renderStatusLabel = (status) => {
    if (status === 'Progreso') return 'En proceso de baja';
    if (status === 'Activo')   return 'Activo';
    if (status === 'Inactivo') return 'Inactivo';
    return status;
  };


   const hasActionable = filtered.some(
    s => !(s.status === 'Inactivo' && s.fecha_baja)
  );

  return (
    <div className="app-container">
      <Container className='my-5'>
        <Card className='shadow-sm'>
          <Card.Header>
            <h5 className='mb-0'>Solicitudes De Baja</h5>
          </Card.Header>
          <Card.Body>
            {/* filtro */}
            <div className="d-flex align-items-center mb-3">
              <label className="me-2 mb-0">Filtrar por Status:</label>
              <Form.Select
                value={filterEstado}
                onChange={e => setFilterEstado(e.target.value)}
                style={{ width: '200px' }}
              >
                <option value="Todos">Todos</option>
                <option value="Activos">Activos</option>
                <option value="Inactivos">Inactivos</option>
              </Form.Select>
            </div>

            {/* tabla */}
            {filtered.length === 0 ? (
              <p className='text-center text-muted'>
                No hay solicitudes para el filtro “{filterEstado}”.
              </p>
            ) : (
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Nombre</th>
                    <th>Fecha</th>
                    <th>RFC</th>
                    <th>Estatus</th>
                    {hasActionable && <th>Acción</th>}
                  </tr>
                </thead>
              <tbody>
             {filtered.map(s => (
              <tr key={s.SolicitudId}>
                <td>{s.Usuario}</td>
                <td>{`${s.nombre_usuario} ${s.apellido_paterno} ${s.apellido_materno}`}</td>
                <td>{new Date(s.Fecha).toLocaleDateString()}</td>
                <td>{s.rfc}</td>
                <td>{renderStatusLabel(s.status)}</td>
                {hasActionable && (
                  <td>
                    {/* Sólo muestro el botón si NO está Inactivo+fecha_baja */}
                    {!(s.status === 'Inactivo' && s.fecha_baja) && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleOpenDetail(s)}
                      >
                        Solicitar baja
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
              </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </Container>

        {/* Modal detalle */}
        <Modal
          show={showDetailModal}
          onHide={handleCloseDetail}
          size="lg"
          centered
          style={{ zIndex: 11000 }}
        >
          {/* 1) Botón Ejecutar en el header */}
          <Modal.Header closeButton>
            <Modal.Title>
              Solicitud de Baja 
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/*Avatar*/}
            <AvatarCentrado src={selected?.foto_url} alt="Foto del vendedor"/>
            {selected?.situacion_rechazo && (
            <>
              <hr />
              <p><strong>Motivo de Rechazo:</strong></p>
              <p style={{ whiteSpace: 'pre-wrap' }}>
                {selected?.situacion_rechazo}
              </p>
            </>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Seleccione motivo</Form.Label>
              <Form.Select
                  value={reasonType}
                  onChange={e => setReasonType(e.target.value)}
              >
                <option value="">-- elige uno --</option>
                <option value="Ausente">Ausente</option>
                <option value="Fin de contrato">Fin de contrato</option>
                <option value="Mala práctica">Mala práctica</option>
                <option value="Otro">Otro</option>
              </Form.Select>
            </Form.Group>{(reasonType === 'Mala práctica' || reasonType === 'Otro') && (
              <Form.Group className="mb-3">
                <Form.Label>Explique la razón</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={reasonText}
                    onChange={e => setReasonText(e.target.value)}
                />
              </Form.Group>
          )}
            <p><strong>Email:</strong> {selected?.Usuario || '-'}</p>
            <p><strong>Nombre:</strong> {selected?.nombre_usuario}</p>
            <p><strong>Apellido Paterno:</strong> {selected?.apellido_paterno}</p>
            <p><strong>Apellido Materno:</strong> {selected?.apellido_materno}</p>
            <p><strong>Teléfono:</strong> {selected?.telefono || '-'}</p>
            <p><strong>Fecha:</strong> {selected && new Date(selected.Fecha).toLocaleDateString()}</p>
            <p><strong>RFC:</strong> {selected?.rfc}</p>
            <p><strong>CVVEN:</strong> {selected?.ccven || '-'}</p>
            <p><strong>Usuario Red:</strong> {selected?.usuario_red || '-'}</p>
            {rolId === 9 && selected?.fgu_toa && (
              <p>
                <strong>TOA:</strong> {selected.fgu_toa}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDetail}>
              Cerrar
            </Button>
            <Button
                variant="danger"
                onClick={handleConfirmReason}
                disabled={
                    !reasonType ||
                    ((reasonType === 'Mala práctica' || reasonType === 'Otro') && !reasonText.trim())
                }
            >
              Ejecutar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
